import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import { store, useAppSelector } from '@/store';
import { lightTheme, darkTheme } from '@/theme';
import App from '@/App';
import { reloadOnce } from '@/utils/reload-once';
import '@/i18n';
// Die Schriften liefert der eigene Server aus. Vorher kamen sie von Google
// Fonts: Jeder Seitenaufruf schickte die IP-Adresse des Browsers an Google.
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/dm-sans/400.css';
import '@fontsource/dm-sans/500.css';
import '@fontsource/dm-sans/600.css';
import '@fontsource/dm-sans/700.css';

/**
 * Ein Bündel lässt sich nicht nachladen — meist, weil auf dem Server seit dem
 * Öffnen des Reiters eine neue Version liegt (Testgrundlage 2.2, B-01). Dann
 * einmal neu laden und die aktuelle Fassung holen. Geht das nicht (gerade erst
 * neu geladen), läuft der Fehler weiter bis zur Fehlergrenze der Seite, und
 * die zeigt einen Hinweis mit „Neu laden“.
 */
window.addEventListener('vite:preloadError', (ereignis) => {
  if (reloadOnce()) ereignis.preventDefault();
});

const ThemedApp: React.FC = () => {
  const themeMode = useAppSelector((state) => state.ui.themeMode);
  const theme = themeMode === 'dark' ? darkTheme : lightTheme;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        // Sechs Sekunden: Nach vier waren Meldungen weg, bevor man sie gelesen
        // hatte (Testgrundlage 2.2, Bedienung U3). Fehler stehen acht
        // (useMeldung, useAktion).
        autoHideDuration={6000}
      >
        <App />
      </SnackbarProvider>
    </ThemeProvider>
  );
};

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Provider store={store}>
      <BrowserRouter>
        <ThemedApp />
      </BrowserRouter>
    </Provider>
  </React.StrictMode>,
);
